import { Component, ViewChild, ElementRef, Output, EventEmitter, signal, input, ChangeDetectionStrategy } from "@angular/core";
import { Router, RouterLink } from "@angular/router";
import { ActivatedRoute } from "@angular/router";
import { Observable } from "rxjs";
import { DateTime } from "luxon";

import { LegendPosition } from "@swimlane/ngx-charts";
import { MatDialog } from "@angular/material/dialog";
import { environment } from "../../../environments/environment";
import { AbstractBuyerComponent } from "../abstract/buyer.component";
import { DatePipe, DecimalPipe } from "@angular/common";
import { CurrencyPipe } from "@angular/common";
import { Account } from "../../entity/Account";
import { AuthService } from "../../service/auth.service";
import { AnalyticsService } from "../../service/analytics.service";
import { GeneralService } from "../../service/general.service";
import { User } from "../../entity/User";
import { RateData } from "../../obj/RateData";
import { CurrentContractData } from "../../obj/CurrentContractData";
import { Contract } from "../../entity/Contract";
import { KilowantBarHomeAcceptOfferChartComponent } from "../chart/kilowant-charts-home-accept-offer.component";
import { Serie } from "../../obj/Serie";
import { PackageData } from "../../obj/PackageData";
import { DiffBenchmark } from "../../obj/DiffBenchmark";

@Component({
    selector: "kilowant-user-current-cost-data",
    templateUrl: "../../../html/incs/user-current-cost-data.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [RouterLink, KilowantBarHomeAcceptOfferChartComponent, DecimalPipe, CurrencyPipe, DatePipe]
})
export class UserCurrentCostDataComponent extends AbstractBuyerComponent {
    // Input signals
    client = input<User | null>(null);
    account = input.required<Account>();
    currentCost = input.required<CurrentContractData>();
    prefix = input<string>('');
    currentPos = input<number>(0);
    totalNumber = input<number>(0);
    forceClose = input<Observable<void>>();
    forceOpen = input<Observable<void>>();
    alwaysOpened = input<boolean>(false);

    hasOffersWithSavings = input<boolean>(false);
    topPackages = input<PackageData[]>([]);
    hasActiveAITask = input<boolean>(false);
    ratesPerPage = input<number>(12);
    max_savings = input<string>('');
    max_savings_date = input<Date | null>(null);

    THIRD_PARTY_SUPPLIER = "3rd Party Supply";

    @Output() switchUtilityAction = new EventEmitter<boolean>();

    @Output() openCloseAction = new EventEmitter<boolean>();

    /**
     * Emits the package the user asked to inspect in detail.
     * Triggered only by an explicit user action:
     *   • click on the "Save $X (Y%)" caption of the utility tile.
     * Button clicks in the tile only update the chart preview and do NOT emit.
     */
    @Output() packageSelected = new EventEmitter<PackageData>();

    @ViewChild('body') bodyElement!: ElementRef;

    // State signals
    viewPriceHistory = signal<boolean>(false);
    priceHistoryLoaded = signal<boolean>(false);
    view = signal<[number, number]>([400, 250]);
    legend = signal<boolean>(true);
    legendTitle = signal<string>('');
    legendPosition = signal<LegendPosition>(LegendPosition.Below);
    showLabels = signal<boolean>(true);
    animations = signal<boolean>(true);
    xAxis = signal<boolean>(true);
    yAxis = signal<boolean>(false);
    showYAxisLabel = signal<boolean>(false);
    showXAxisLabel = signal<boolean>(false);
    xAxisLabel = signal<string>('Year');
    yAxisLabel = signal<string>('');
    colorScheme = signal({ domain: ['#58504e'], name: 'kilowant', selectable: true, group: null! });

    rates = signal<RateData[]>([]);
    /** Untouched copy of the rates as returned from the API (before any 3rd-Party serie is appended). */
    baseRates = signal<RateData[]>([]);
    startRates = signal<number>(0);
    chartLineName = signal<string>('');

    paying_diff = signal<number | null>(null);
    diff_start = signal<string | null>(null);

    pagedRates = signal<RateData[]>([]);
    utilityAverageForHistory = signal<number>(0);
    communityAverageForHistory = signal<number>(0);
    historyContractVals = signal<number[]>([]);
    historyContractShiftsLeft = signal<number[]>([]);
    historyContractShiftsRight = signal<number[]>([]);
    historicalContracts: Contract[] = [];

    supplierLogoUrl = signal<string | null>(null);

    selectedDatasets = signal<number[]>([]);

    latestUtilityDate = signal<string>('');
    latestCommunityDate = signal<string>('');
    firstLoadPriceHistory = signal<boolean>(false);

    future_price = signal<number>(0);
    future_months = signal<number>(0);

    /** Index of the currently highlighted package button (null = none). */
    activePackageIndex = signal<number | null>(null);

    private forceCloseSub: any;
    private forceOpenSub: any;
    private timeoutId: any;

    constructor(
        router: Router,
        route: ActivatedRoute,
        dialog: MatDialog,
        protected datepipe: DatePipe,
        protected currencypipe: CurrencyPipe,
        authService: AuthService,
        analyticsService: AnalyticsService,
        protected numberpipe: DecimalPipe,
        generalService: GeneralService
    ) { super(router, route, dialog, authService, analyticsService, generalService); }

    ngOnInit(): void {
        super.ngOnInit();
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        if (this.forceCloseSub) this.forceCloseSub.unsubscribe();
        if (this.forceOpenSub) this.forceOpenSub.unsubscribe();
        if (this.timeoutId) clearTimeout(this.timeoutId);
    }

    ngAfterContentInit(): void {
    }

    pageStart() {
        this.viewPriceHistory.set(false);
        this.loadSupplierLogo();
        this.selectedDatasets.set([1]);
        if (this.account().isCommunity) {
            this.selectedDatasets.update(arr => [...arr, 2]);
        }
        this.loadEstimatedDates();
        this.timeoutId = setTimeout(() => {
            this.firstLoadPriceHistory.set(true);
            this.loadRatesDistribution();
        }, 1000);
        this.forceCloseSub = this.forceClose()?.subscribe(() => {
            if (this.viewPriceHistory()) {
                this.changeViewPriceHistory();
            }
        });
        this.forceOpenSub = this.forceOpen()?.subscribe(() => {
            if (!this.viewPriceHistory()) {
                this.changeViewPriceHistory();
            }
        });
    }

    /** True when the package buttons should be rendered inside the utility tile. */
    showPackageButtons(): boolean {
        return this.totalNumber()==1 &&
            this.currentCost().isRate
            && this.hasOffersWithSavings()
            && this.topPackages() != null
            && this.topPackages().length > 0;
    }

    /**
     * Toggles the package selection:
     *  - Click on a not-yet-selected package → highlight it immediately and
     *    reload rates for the package's period (see `loadPackageRates`).
     *  - Click on the already-selected package → unselect. The currently loaded
     *    rates are kept (from the previously selected package's period); only
     *    the 3rd-Party serie and isInactive flags are removed. No new API call.
     *  - Click on a different package → switch selection and reload rates for
     *    the new package's period.
     */
    selectPackageButton(pkg: PackageData, index: number): void {
        const current = this.activePackageIndex();

        if (current === index) {
            // Unselect — keep current rates, remove 3rd-Party + isInactive flags.
            this.activePackageIndex.set(null);
            this.applyPackageToRates(null);
            return;
        }

        // Select — highlight immediately for responsiveness, then reload rates.
        this.activePackageIndex.set(index);
        this.loadPackageRates(pkg, index);
    }

    /**
     * True only when the caption currently shows a package's savings — i.e. a
     * package is selected, we're in the "offers with savings" state, and no AI
     * task is overriding the caption.
     */
    isSaveTextClickable(): boolean {
        if (!this.currentCost().isRate) { return false; }
        if (this.currentCost().saveVsEnergy < 0) { return false; }
        if (!this.hasOffersWithSavings()) { return false; }
        if (this.hasActiveAITask()) { return false; }
        return this.activePackageIndex() != null;
    }

    /**
     * Fires when the user clicks the "Save $X (Y%)" caption.
     * Emits the currently selected package via `packageSelected` so the
     * parent can jump to its details.
     */
    onSaveTextClick(): void {
        if (!this.isSaveTextClickable()) { return; }
        const idx = this.activePackageIndex();
        const pkgs = this.topPackages();
        if (idx == null || pkgs == null || idx < 0 || idx >= pkgs.length) { return; }
        this.changeViewPriceHistory();
        this.packageSelected.emit(pkgs[idx]);
    }

    /**
     * Renames any "Kilowant" serie to `THIRD_PARTY_SUPPLIER` when the account's
     * own Kilowant price plan is losing against the utility (`saveVsEnergy < 0`).
     *
     * This keeps the chart legend consistent with the caption wording
     * ("vs. lowest available 3rd Party Supply") and removes the Kilowant brand
     * from the more-expensive bar. The rename is applied at load time to both
     * `rates` and `baseRates`, so it survives every `applyPackageToRates`
     * rebuild without having to be re-applied.
     */
    private renameKilowantToThirdParty(rates: RateData[]): RateData[] {
        if (rates == null) { return rates; }
        if (!(this.currentCost().saveVsEnergy < 0)) { return rates; }
        for (const r of rates) {
            if (r.series == null) { continue; }
            for (const s of r.series) {
                if (s.name === "Kilowant") {
                    s.name = this.THIRD_PARTY_SUPPLIER;
                }
            }
        }
        return rates;
    }

    /**
     * Rebuilds `rates` from `baseRates` and — when a package is supplied —
     * appends a this.THIRD_PARTY_SUPPLIER serie to every monthly rate that falls inside one of
     * the package offer's terms, then applies the `isInactive` flags.
     *
     * `isInactive` rules (only while a package is selected):
     *   • Months covered by the package (a 3rd-Party serie is present)
     *     → every other serie in that rate gets `isInactive = true`;
     *       the 3rd-Party serie itself stays active.
     *   • Months not covered by the package
     *     → the "wrong" benchmark is marked inactive:
     *       - community account  → RI Energy is inactive
     *       - utility   account  → NextEra Energy is inactive
     *
     * When no package is selected, all `isInactive` flags are cleared.
     */
    private applyPackageToRates(pkg: PackageData | null): void {
        // Package buttons only make sense for the "utility-only" tile.
        if (!this.currentCost().isRate) { return; }

        const base = this.baseRates();
        if (base == null || base.length === 0) { return; }

        // 1) Pre-compute the offer windows (start / end Date) once.
        interface OfferWindow { start: DateTime; end: DateTime; price: number; }
        const windows: OfferWindow[] = [];
        if (pkg != null && pkg.offers != null) {
            for (const od of pkg.offers) {
                if (od == null || od.offer == null || od.offer.termFrom == null) { continue; }
                const start = DateTime.fromJSDate(od.offer.termFrom).startOf('month');
                const end = start.plus({ months: od.offer.termPeriod }).minus({ days: 1 }).endOf('month');
                windows.push({ start, end, price: od.offer.priceForBuyer() });
            }
        }

        const isCommunity = this.account()?.isCommunity === true;
        const packageSelected = windows.length > 0;

        // 2) Clone base rates (fresh Serie instances so `isInactive` is safe to mutate),
        //    append the 3rd-Party serie where applicable, then apply isInactive flags.
        const newRates: RateData[] = [];
        for (const r of base) {
            const newRate = new RateData(r.name, []);
            if (r.series != null) {
                for (const s of r.series) {
                    newRate.series.push(this.cloneSerie(s));
                }
            }

            let hasThirdParty = false;
            if (windows.length > 0) {
                const parts = (r.name ?? '').split('/');
                const m = parseInt(parts[0]);
                const y = parseInt(parts[1]);
                if (!isNaN(m) && !isNaN(y)) {
                    const rateMonth = DateTime.local(y, m, 1);
                    for (const w of windows) {
                        if (rateMonth >= w.start && rateMonth <= w.end) {
                            newRate.series.push(new Serie(this.THIRD_PARTY_SUPPLIER, w.price));
                            hasThirdParty = true;
                            break; // one 3rd-Party serie per month
                        }
                    }
                }
            }

            if (packageSelected) {
                if (hasThirdParty) {
                    // Every existing serie in this rate is dimmed, but the
                    // 3rd-Party serie (the package price) must stay active.
                    for (const s of newRate.series) {
                        if (s.name !== this.THIRD_PARTY_SUPPLIER) {
                            s.isInactive = true;
                        }
                    }
                } else {
                    // Only the "wrong" benchmark is marked inactive.
                    for (const s of newRate.series) {
                        if (isCommunity) {
                            if (s.name === "RI Energy") { s.isInactive = true; }
                        } else {
                            if (s.name === "NextEra Energy") { s.isInactive = true; }
                        }
                    }
                }
            }
            // else: `cloneSerie` already resets isInactive to false.

            newRates.push(newRate);
        }

        // 3) Swap in the rebuilt rates and refresh the paged window.
        this.rates.set(newRates);
        this.updateRates();
    }

    /** Fresh copy of a Serie, keeping the source value/forecast flag but resetting isInactive. */
    private cloneSerie(s: Serie): Serie {
        const newSerie = new Serie(s.name, s.value);
        (newSerie as any).isForecasted = (s as any).isForecasted;
        (newSerie as any).isInactive = false;
        return newSerie;
    }

    /**
     * Initial rate load.
     *  • Utility-only with packages → auto-select the first package and load
     *    rates for its period via `loadPackageRates`.
     *  • Otherwise → load rates for the default window derived from
     *    `currentCost` (term dates / rateEnd).
     */
    loadRatesDistribution(): void {
        if (!this.bodyElement || !this.account()) { return; }
        this.view.set([this.bodyElement.nativeElement.offsetWidth, 250]);

        if (this.isOnUtilityOnly() && this.topPackages() != null && this.topPackages().length > 0) {
            // First package auto-selected — its period also defines the window.
            this.loadPackageRates(this.topPackages()[0], 0);
            return;
        }

        this.activePackageIndex.set(null);
        const period = this.getDefaultPeriod();
        this.fetchRatesAndSetup(period.from, period.to);
    }

    /** Default rate window derived from the current cost (used when no package is driving the chart). */
    private getDefaultPeriod(): { from: Date | null, to: Date | null } {
        let to: Date | null = null;
        if (this.currentCost().termTo != null) {
            to = DateTime.fromJSDate(this.currentCost().termTo!).minus({ days: 1 }).toJSDate();
        }
        let from: Date | null = this.currentCost().termFrom;

        if (this.currentCost().isRate && to == null && this.currentCost().rateEnd != null) {
            to = DateTime.fromJSDate(this.currentCost().rateEnd!).toJSDate();
        }
        if (this.currentCost().isRate && to == null) {
            to = this.currentDate.plus({ month: 11 }).toJSDate();
        }
        if (this.currentCost().isRate && from != null && DateTime.fromJSDate(from) <= this.currentDate.plus({ days: 1 })) {
            from = null;
        }

        return { from, to };
    }

    /** Fetches rates for the given window and sets up the chart (no package applied). */
    private fetchRatesAndSetup(from: Date | null, to: Date | null): void {
        let isCommunity = this.currentCost().compareToCommunity;
        let noKilowantPrice = false;
        if (!this.currentCost().isRate && !this.currentCost().isKilowantContract && !isCommunity) {
            noKilowantPrice = true;
        }

        this.generalService.getHistoricalRatesPerAccount(
            this.account(), from, to, !this.currentCost().isRate,
            false, noKilowantPrice, true, true
        ).then((data: any) => {
            // Rename "Kilowant" → "3rd Party Supply" when the contract is more
            // expensive than the utility (see method doc for rationale).
            const renamed = this.renameKilowantToThirdParty(data);

            this.rates.set(renamed);
            this.baseRates.set(this.cloneRates(renamed));

            if (this.currentCost().isRate) {
                this.estimateFutureSavings();
            } else {
                this.estimateSavings();
            }

            const newStart = (this.rates().length - this.ratesPerPage());
            this.startRates.set(newStart < 0 ? 0 : newStart);
            this.chartLineName.set('Price Plan');
            this.priceHistoryLoaded.set(true);
            this.updateRates();
        });
    }

    /**
     * Fetches rates for a specific package's period (min start → max end across
     * all of that package's offers) and applies that package on top of them.
     *
     * Called every time a *new* package is selected. Not called on unselect —
     * unselect reuses the currently loaded rates via `applyPackageToRates(null)`.
     */
    private loadPackageRates(pkg: PackageData, index: number): void {
        if (pkg == null || pkg.offers == null || pkg.offers.length === 0) { return; }
        if (!this.bodyElement || !this.account()) { return; }

        this.view.set([this.bodyElement.nativeElement.offsetWidth, 250]);

        // Utility-only tile always has currentCost().isRate === true,
        // so `noKilowantPrice` is always false here.
        this.generalService.getHistoricalRatesPerAccount(
            this.account(), pkg.startDate, this.getTermTo(pkg.offers[pkg.offers.length - 1].offer.termFrom, pkg.offers[pkg.offers.length - 1].offer.termPeriod),
            !this.currentCost().isRate,
            false,
            true, //no kilowant
            true, // show utility
            true  // show community
        ).then((data: any) => {
            // Same rename rule as in fetchRatesAndSetup — keeps `baseRates`
            // and `rates` consistent so `applyPackageToRates` rebuilds cleanly.
            const renamed = this.renameKilowantToThirdParty(data);

            this.rates.set(renamed);
            this.baseRates.set(this.cloneRates(renamed));
            this.activePackageIndex.set(index);

            if (this.currentCost().isRate) {
                this.estimateFutureSavings();
            } else {
                this.estimateSavings();
            }

            const newStart = (this.rates().length - this.ratesPerPage());
            this.startRates.set(newStart < 0 ? 0 : newStart);
            this.chartLineName.set('Price Plan');
            this.priceHistoryLoaded.set(true);

            // Apply the 3rd-Party serie + isInactive flags for this package.
            this.applyPackageToRates(pkg);
        });
    }

    /** Shallow clone of a rates array, preserving the Serie references. */
    private cloneRates(source: RateData[]): RateData[] {
        const result: RateData[] = [];
        if (source == null) { return result; }
        for (const r of source) {
            const newRate = new RateData(r.name, []);
            if (r.series != null) {
                for (const s of r.series) {
                    newRate.series.push(s);
                }
            }
            result.push(newRate);
        }
        return result;
    }

    getPreviousDay(inDate: Date | null): Date | null {
        if (inDate != null) {
            return DateTime.fromJSDate(inDate).minus({ days: 1 }).toJSDate();
        }
        return inDate;
    }

    changeViewPriceHistory(): void {
        if (this.alwaysOpened() && this.viewPriceHistory()) {
            return;
        }
        this.viewPriceHistory.update(v => !v);
        this.openCloseAction.next(this.viewPriceHistory());
    }

    estimateSavings(): void {
        this.paying_diff.set(0);
        this.diff_start.set(null);
        let total_avg_kilowant = 0;
        let total_avg_utility = 0;
        const ratesArray = this.rates();
        for (let i = 0; i < ratesArray.length; i++) {
            if (ratesArray[i].series.length > 1) {
                let kilowant_arr: number[] = [];
                let utility_arr: number[] = [];
                for (let x = 0; x < ratesArray[i].series.length; x++) {
                    if (ratesArray[i].series[x].name == this.THIRD_PARTY_SUPPLIER) {
                        if (this.diff_start() == null) {
                            this.diff_start.set(ratesArray[i].name);
                        }
                        kilowant_arr.push(ratesArray[i].series[x].value);
                    } else {
                        utility_arr.push(ratesArray[i].series[x].value);
                    }
                }
                if (this.estimateAvg(kilowant_arr) > 0 && this.estimateAvg(utility_arr) > 0) {
                    total_avg_kilowant += this.estimateAvg(kilowant_arr);
                    total_avg_utility += this.estimateAvg(utility_arr);
                }
            }
        }
        if (total_avg_utility > 0) {
            this.paying_diff.set(Math.round((total_avg_utility - total_avg_kilowant) / total_avg_utility * 100));
        }
    }

    historyLeft(): void {
        if (this.startRates() > 0) {
            this.startRates.update(val => {
                let newVal = val - this.ratesPerPage();
                return newVal < 0 ? 0 : newVal;
            });
            this.updateRates();
        }
    }

    historyRight(): void {
        if (this.startRates() < this.rates().length - this.ratesPerPage()) {
            this.startRates.update(val => {
                let newVal = val + this.ratesPerPage();
                let max = this.rates().length - this.ratesPerPage();
                return newVal > max ? max : newVal;
            });
            this.updateRates();
        }
    }

    updateRates(): void {
        const ratesArray = this.rates();
        const start = this.startRates();
        const perPage = this.ratesPerPage();
        const newPagedRates: RateData[] = [];
        let utilityAvg = 0;
        let communityAvg = 0;
        let i = start;
        let n = 0;
        while (i < ratesArray.length && n < perPage) {
            newPagedRates.push(ratesArray[i]);
            if (!this.currentCost().isRate) {
                newPagedRates[newPagedRates.length - 1].series.push(
                    new Serie("Price Plan " + (new CurrencyPipe("en-US")).transform(this.currentCost().price!, "USD", "symbol", "1.3-3") + "/kWh", this.currentCost().price!)
                );
            }
            i++;
            n++;
        }
        this.pagedRates.set(newPagedRates);

        if (!this.currentCost().isRate) {
            if (this.account()?.isCommunity) {
                communityAvg = this.currentCost().communityRate || 0;
            }
            utilityAvg = this.currentCost().utilityRate || 0;
        }
        this.utilityAverageForHistory.set(utilityAvg);
        this.communityAverageForHistory.set(communityAvg);

        const historyVals: number[] = [];
        const shiftsLeft: number[] = [];
        const shiftsRight: number[] = [];
        const ratesFrom = this.getDateFromRatesString(newPagedRates[0]?.name || '', false);
        const ratesTo = this.getDateFromRatesString(newPagedRates[newPagedRates.length - 1]?.name || '', true);

        if (this.currentCost().isRate) {
            if (this.historicalContracts) {
                for (let i = 0; i < this.historicalContracts.length; i++) {
                    const contractTermFrom = DateTime.fromJSDate(this.historicalContracts[i].termFrom);
                    const contractTermTo = DateTime.fromJSDate(this.historicalContracts[i].termTo).minus({ days: 1 });
                    if (contractTermFrom <= ratesTo && contractTermTo >= ratesFrom) {
                        historyVals.push(this.historicalContracts[i].getSalesPrice());
                        let leftShift = 0;
                        if (contractTermFrom > ratesFrom) {
                            leftShift = this.getDiffPeriodicityWise(contractTermFrom, ratesFrom, '');
                        }
                        shiftsLeft.push(leftShift);
                        let rightShift = 0;
                        if (contractTermTo < ratesTo) {
                            rightShift = this.getDiffPeriodicityWise(ratesTo, contractTermTo, '');
                        }
                        shiftsRight.push(rightShift);
                    }
                }
            }
        } else {
            const contractTermFrom = DateTime.fromJSDate(this.currentCost().termFrom!);
            const contractTermTo = DateTime.fromJSDate(this.currentCost().termTo!).minus({ days: 1 });
            if (contractTermFrom <= ratesTo && contractTermTo >= ratesFrom) {
                historyVals.push(this.currentCost().price!);
                shiftsLeft.push(0);
                shiftsRight.push(0);
            }
        }
        this.historyContractVals.set(historyVals);
        this.historyContractShiftsLeft.set(shiftsLeft);
        this.historyContractShiftsRight.set(shiftsRight);
    }

    loadSupplierLogo(): void {
        if (this.currentCost().isRate) {
            let url = environment.WEB_API_URL + "Utility/" + this.account().utility!.id + "/Logo";
            if (this.account()?.isCommunity) {
                url += "?community=true";
            }
            this.supplierLogoUrl.set(url);
        } else if (this.currentCost().supplierId != null) {
            this.supplierLogoUrl.set(environment.WEB_API_URL + "Company/" + this.currentCost().supplierId! + "/Logo");
        }
    }

    loadEstimatedDates(): void {
        this.generalService.getAccountGetLatestUtilityDate(this.account().id).then(x => {
            if (x.key != null) {
                this.latestUtilityDate.set(this.datepipe.transform(x.key, 'MMM. y')!);
            }
            if (x.value != null) {
                this.latestCommunityDate.set(this.datepipe.transform(x.value, 'MMM. y')!);
            }
        });
    }

    contractText(currentData: CurrentContractData): string {
        let outText = 'Previous Price Plan';
        if (this.isPeriodCurrent(currentData.termFrom, currentData.termTo)) {
            outText = 'Current Price Plan';
        }
        if (this.isPeriodFuture(currentData.termFrom, currentData.termTo)) {
            outText = 'Future Price Plan';
        }
        if (this.totalNumber() > 1) {
            outText += ' ' + this.currentPos() + ' of ' + this.totalNumber();
        }
        return outText;
    }

    isPeriodCurrent(termFrom: Date | null, termTo: Date | null): boolean {
        return ((termFrom == null || this.applySitesTimeZone(DateTime.fromJSDate(termFrom)).startOf('day') <= this.currentDate.startOf('day'))) && (termTo == null || (this.applySitesTimeZone(DateTime.fromJSDate(termTo)).startOf('day') >= this.currentDate.startOf('day')));
    }

    isPeriodFuture(termFrom: Date | null, termTo: Date | null): boolean {
        return (termFrom != null && this.applySitesTimeZone(DateTime.fromJSDate(termFrom)).startOf('day') > this.currentDate.startOf('day'));
    }

    getDiffPeriodicityWise(fromDate: DateTime, toDate: DateTime, periodicity: string): number {
        if (periodicity == null || periodicity == '' || periodicity == 'monthly') {
            return Math.floor(fromDate.diff(toDate, ['months'])['months']);
        }
        if (periodicity == 'quarterly') {
            return Math.floor(fromDate.diff(toDate, ['months'])['months'] / 3);
        }
        if (periodicity == 'yearly') {
            return Math.floor(fromDate.diff(toDate, ['years'])['years']);
        }
        return 0;
    }

    estimateFutureSavings(): void {
        this.paying_diff.set(0);
        this.diff_start.set(null);
        let total_avg_kilowant = 0;
        let total_avg_utility = 0;
        let futurePrice = 0;
        let futureMonths = 0;
        const ratesArray = this.rates();
        for (let i = 0; i < ratesArray.length; i++) {
            const arr = ratesArray[i].name.split('/');
            const month = parseInt(arr[0]);
            const year = parseInt(arr[1]);
            if (DateTime.local(year, month, 1) > this.currentDate) {
                futureMonths += 1;
                for (let x = 0; x < ratesArray[i].series.length; x++) {
                    if (ratesArray[i].series[x].name != this.THIRD_PARTY_SUPPLIER) {
                        futurePrice += ratesArray[i].series[x].value;
                    }
                }
            }

            if (ratesArray[i].series.length > 1) {
                let kilowant_arr: number[] = [];
                let utility_arr: number[] = [];
                for (let x = 0; x < ratesArray[i].series.length; x++) {
                    if (DateTime.local(year, month, 1) < this.currentDate) {
                        if (ratesArray[i].series[x].name == this.THIRD_PARTY_SUPPLIER) {
                            if (this.diff_start() == null) {
                                this.diff_start.set(DateTime.local(year, month, 1).monthShort + ' ' + year);
                            }
                            kilowant_arr.push(ratesArray[i].series[x].value);
                        } else {
                            utility_arr.push(ratesArray[i].series[x].value);
                        }
                    }
                }
                if (this.estimateAvg(kilowant_arr) > 0 && this.estimateAvg(utility_arr) > 0) {
                    total_avg_kilowant += this.estimateAvg(kilowant_arr);
                    total_avg_utility += this.estimateAvg(utility_arr);
                }
            }
        }
        if (futureMonths > 0) {
            futurePrice = futurePrice / futureMonths;
        }
        this.future_price.set(futurePrice);
        this.future_months.set(futureMonths);
        if (total_avg_utility > 0) {
            this.paying_diff.set(Math.round((total_avg_utility - total_avg_kilowant) / total_avg_utility * 100));
        }
    }

    getColorBasedOnUtility(): string {
        if (this.account() == null) {
            return '';
        }
        if (this.account().isCommunity) {
            return environment.RI_COMMUNITY_COLOR;
        }
        return environment.RI_UTILITY_COLOR;
    }

    changeCurrentElectricityPlan(): void {
        this.switchUtilityAction.next(true);
    }

    isOnUtilityOnly(): boolean {
        if ((this.totalNumber() == 1) && this.currentCost().isRate && this.currentCost().rateEnd != null) {
            return true;
        }
        return false;
    }

    /**
     * Format a package's savings against the account's benchmark the same way
     * user-home does: "Save $X (Y%)" when USD savings are available,
     * "Save Y%" when only percentages are available, else "Lock Rate".
     */
    private getPackageSavingsText(pkg: PackageData | null): string {
        if (pkg == null || pkg.savings == null) { return "Lock Rate"; }

        const benchmark = this.account()?.isCommunity ? DiffBenchmark.COMMUNITY : DiffBenchmark.UTILITY;
        const s = pkg.savings[benchmark];
        if (s == null) { return "Lock Rate"; }

        if (s.diffVsBenchmarkInUSD != null && s.diffVsBenchmarkInUSD > 0) {
            return "Save $" + this.numberpipe.transform(s.diffVsBenchmarkInUSD, "", "en-US")
                + " (" + s.diffVsBenchmark + "%)";
        }
        if (s.diffVsBenchmark != null && s.diffVsBenchmark > 0) {
            return "Save " + s.diffVsBenchmark + "%";
        }
        return "Lock Rate";
    }

    isCaptionBold(): boolean {
        const benchmark = this.account()?.isCommunity ? DiffBenchmark.COMMUNITY : DiffBenchmark.UTILITY;
        return this.totalNumber()==1 && this.topPackages() && this.topPackages().length > 0 && this.topPackages()[0].savings[benchmark].diffVsBenchmark != null && (this.topPackages()[0].savings[benchmark].diffVsBenchmark! > 0);
    }

    getTextForTitle(): string {
        if (this.currentCost().saveVsEnergy < 0) {
            const c = this.currentCost();
            if (c == null) { return ""; }
            const parts: string[] = [];
            parts.push("You are projected to save");

            if (!this.account().isCommunity) {
                // Utility clause (RI Energy)
                if (c.saveVsEnergyInUSD < 0) {
                    parts.push("$" + this.numberpipe.transform(c.saveVsEnergyInUSD * -1, "", "en")
                        + " (" + c.saveVsEnergy * -1 + "%) vs. lowest available 3rd Party Supply Price");
                } else if (c.saveVsEnergy < 0) {
                    parts.push(c.saveVsEnergy * -1 + "% vs. lowest available 3rd Party Supply Price");
                }
            } else {
                // Community clause (NextEra Energy) — only for community accounts
                if (c.saveVsCommunity != null && c.saveVsCommunity < 0) {
                    if (c.saveVsCommunityInUSD < 0) {
                        parts.push("$" + this.numberpipe.transform(c.saveVsCommunityInUSD * -1, "", "en")
                            + " (" + c.saveVsCommunity * -1 + "%) vs. lowest available 3rd Party Supply Price");
                    } else if (c.saveVsCommunity < 0) {
                        parts.push(c.saveVsCommunity * -1 + "% vs. lowest available 3rd Party Supply Price");
                    }
                }
            }
            return parts.join(" ");
        }

        if (this.hasOffersWithSavings()) {
            // If a package is currently selected, show its savings instead of
            // the generic call-to-action. Mirrors user-home's tile text.
            const pkgIndex = this.activePackageIndex();
            const pkgs = this.topPackages();
            if (pkgIndex != null && pkgs != null && pkgIndex >= 0 && pkgIndex < pkgs.length) {
                return this.getPackageSavingsText(pkgs[pkgIndex]);
            }

            return "";
        }
        return "";
    }

    getTextForCurrentStateInfo(): string {
        if (!this.currentCost().isRate) {
            return "You're currently on the lowest price plan available";
        }
        if (this.hasOffersWithSavings()) {
            return "kilowant is " + ((this.hasActiveAITask()) ? "shopping" : "searching") + " for a lower price";
        }
        return "You're currently on the lowest price plan available";        
    }
}